import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Image as ImageIcon,
  VideoCamera,
  MicrophoneStage,
  TextT,
  Hash,
  MapPin,
  Smiley,
  X,
  CheckCircle,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { disciplines as mockDisciplines } from "@/data/mockData";
import { api, formatApiError, API_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";

const types = [
  { key: "image", label: "Imagen", icon: ImageIcon },
  { key: "video", label: "Video", icon: VideoCamera },
  { key: "audio", label: "Audio", icon: MicrophoneStage },
  { key: "text", label: "Texto", icon: TextT },
];

function mediaUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function Publish() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { disciplines: liveDisciplines } = useBrand();
  const disciplines = (liveDisciplines && liveDisciplines.length) ? liveDisciplines : mockDisciplines;
  const fileRef = useRef(null);
  const [kind, setKind] = useState("image");
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState(["#wip"]);
  const [draft, setDraft] = useState("");
  const [discipline, setDiscipline] = useState("artes-visuales");
  const [mediaPath, setMediaPath] = useState(null); // backend-returned url (e.g. /api/uploads/files/xxx.jpg)
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const addTag = () => {
    const t = draft.trim().replace(/^#/, "");
    if (t && !tags.includes(`#${t}`)) setTags([...tags, `#${t}`]);
    setDraft("");
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaPath(data.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async () => {
    if (!user) {
      navigate("/ingresar");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/posts", {
        kind,
        caption: caption.trim(),
        media_url: kind === "text" ? null : mediaPath,
        tags,
      });
      navigate(`/post/${data.post.id}`);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="publish-page">
        <header className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] flex items-center justify-between p-4 md:p-6">
          <div>
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-1">
              NUEVA PUBLICACIÓN
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-black tracking-tighter uppercase">
              Publicar
            </h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="h-10 w-10 border border-[#27272A] inline-flex items-center justify-center hover:border-[#FF0033] hover:text-[#FF0033]"
          >
            <X size={16} />
          </button>
        </header>

        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6">
          <div>
            <div className="grid grid-cols-4 gap-2 mb-6">
              {types.map((t) => {
                const Icon = t.icon;
                const active = kind === t.key;
                return (
                  <button
                    key={t.key}
                    data-testid={`publish-type-${t.key}`}
                    onClick={() => {
                      setKind(t.key);
                      if (t.key === "text") setMediaPath(null);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 border transition-colors ${
                      active
                        ? "border-[#FF3E00] bg-[#FF3E00]/10 text-[#FF3E00]"
                        : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#F4F4F5]"
                    }`}
                  >
                    <Icon size={22} weight={active ? "fill" : "regular"} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {kind !== "text" ? (
              mediaPath ? (
                <div className="relative border border-[#27272A] bg-[#0a0a0a]">
                  <img
                    src={mediaUrl(mediaPath)}
                    alt=""
                    className="w-full max-h-[60vh] object-contain"
                  />
                  <button
                    onClick={() => setMediaPath(null)}
                    className="absolute top-2 right-2 h-8 w-8 bg-[#FF0033] text-white inline-flex items-center justify-center"
                  >
                    <X size={14} />
                  </button>
                  <div className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-[0.22em] bg-black/70 text-[#00FF66] px-2 py-1 inline-flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> SUBIDO
                  </div>
                </div>
              ) : (
                <label
                  data-testid="upload-dropzone"
                  className="block border-2 border-dashed border-[#27272A] hover:border-[#FF3E00] aspect-video bg-[#0a0a0a] flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors p-6"
                >
                  <div className="h-14 w-14 border border-[#FF3E00] text-[#FF3E00] inline-flex items-center justify-center">
                    <ImageIcon size={24} />
                  </div>
                  <div className="font-display font-bold text-lg uppercase tracking-tight">
                    {uploading ? "Subiendo…" : "Arrastra o haz clic para subir"}
                  </div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                    JPG · PNG · MP4 · MP3 · MAX 20MB
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,video/*,audio/*"
                    onChange={handleFile}
                    className="hidden"
                  />
                </label>
              )
            ) : null}

            <div className="mt-6">
              <label className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                // DESCRIPCIÓN
              </label>
              <textarea
                data-testid="publish-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={5}
                placeholder="Cuéntanos qué es esta obra..."
                className="mt-2 w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-4 font-display text-base resize-none"
              />
              <div className="flex items-center justify-between mt-1 font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                <span>{caption.length}/500</span>
                <div className="flex items-center gap-2">
                  <Smiley size={14} />
                  <Hash size={14} />
                  <MapPin size={14} />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                // HASHTAGS
              </label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  placeholder="agregar hashtag..."
                  className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-3 py-2 font-mono text-sm"
                />
                <button
                  onClick={addTag}
                  className="font-mono uppercase text-[10px] tracking-[0.2em] px-4 py-2 border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black"
                >
                  + Añadir
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] bg-[#FF3E00] text-white px-2 py-1"
                  >
                    {t}
                    <button
                      onClick={() => setTags(tags.filter((x) => x !== t))}
                      className="hover:text-white"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="border border-[#27272A] p-3 bg-[#0a0a0a]">
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                // PREVIEW
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-9 w-9 bg-[#121212] border border-[#27272A] overflow-hidden">
                  {user?.photo ? (
                    <img src={user.photo} alt="" className="w-full h-full object-cover" />
                  ) : null}
                </div>
                <div>
                  <div className="font-display font-bold text-sm">
                    {user?.name || "Tú"}
                  </div>
                  <div className="font-mono text-[9px] text-[#A1A1AA]">
                    @{user?.handle || "tu"} · ahora
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#E5E5E5] min-h-[3em] leading-snug">
                {caption || (
                  <span className="text-[#525252] italic">Tu descripción aparecerá aquí...</span>
                )}
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.map((t) => (
                  <span key={t} className="font-mono text-[9px] text-[#FF3E00] uppercase">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="border border-[#27272A]">
              <div className="p-4 border-b border-[#27272A]">
                <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-2">
                  // DISCIPLINA
                </div>
                <select
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none p-2 font-mono text-sm"
                >
                  {disciplines.map((d) => (
                    <option key={d.slug} value={d.slug}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center justify-between p-4 border-b border-[#27272A] cursor-pointer">
                <span className="font-display text-sm">Permitir comentarios</span>
                <input type="checkbox" defaultChecked className="accent-[#FF3E00]" />
              </label>
              <label className="flex items-center justify-between p-4 cursor-pointer">
                <span className="font-display text-sm">Sensible / NSFW</span>
                <input type="checkbox" className="accent-[#FF3E00]" />
              </label>
            </div>

            {error ? (
              <div
                data-testid="publish-error"
                className="border border-[#FF0033] bg-[#FF0033]/10 px-3 py-2 font-mono text-[11px] text-[#FF8899] tracking-wider"
              >
                {error}
              </div>
            ) : null}

            <button
              data-testid="publish-submit"
              disabled={submitting || uploading || (kind !== "text" && !mediaPath)}
              onClick={onSubmit}
              className={`btn-brutal w-full font-mono uppercase text-xs tracking-[0.22em] font-bold py-4 ${
                submitting || uploading || (kind !== "text" && !mediaPath)
                  ? "bg-[#121212] text-[#525252] cursor-not-allowed"
                  : "bg-[#FF3E00] text-white"
              }`}
            >
              {submitting ? "Publicando…" : "→ PUBLICAR AHORA"}
            </button>
            {!user ? (
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                → Debes <a href="/ingresar" className="text-[#FF3E00]">ingresar</a> para publicar.
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </Layout>
  );
}
